import { Api, ApiLike } from "../utils/fetch";

export interface ClientData {
    cod_farmacia: string;
    razon_social: string;
    direccion: string;
    localidad: string;
    provincia: string;
    telefono: string;
    email: string;
    qr?: string;
}

export interface CreditStatus {
    limite: number;
    utilizado: number;
    disponible: number;
}

export interface CreditVerification {
    aprobado: boolean;
    disponible: number;
    monto_solicitado: number;
}

export interface CurrentAccountStatus {
    saldo: number;
    limite_credito: number;
    disponible: number;
}

export interface CurrentAccountMovement {
    fecha: string;
    concepto: string;
    debe: number;
    haber: number;
    saldo: number;
}

export interface Voucher {
    id: number;
    fecha: string;
    tipo: string;
    numero: string;
    importe: number;
}

export interface Summary {
    id: number;
    fecha: string;
    descripcion: string;
    importe: number;
}

export interface Receipt {
    id: number;
    fecha: string;
    numero: string;
    importe: number;
    descripcion: string;
}

export interface CardSettlement {
    id: number;
    fecha: string;
    tarjeta: string;
    importe: number;
    estado: string;
}

export interface EnabledCard {
    id: number;
    nombre: string;
    habilitada: boolean;
    tipo: string;
}

export interface Claim {
    idreclamoweb: number;
    fecha: string;
    motivo: string;
    estado: string;
    comprobante: string;
    mensaje: string;
    tareas: any[];
    [key: string]: any;
}

export interface ClaimReason {
    idreclamoweb_motivo: number;
    nombre: string;
    carga_detalle: number;
}

export interface ClaimLaboratory {
    id_laboratorio: number;
    nombre: string;
    rubro: string;
}

export interface PaymentRegistration {
    monto: number;
    forma_pago: string;
    comprobante: string;
}

export interface PaymentReport {
    monto: number;
    fecha_pago: string;
    observaciones: string;
}

export class ClientAccountService {
    // --- Client Data ---
    static async getClientData() {
        return Api.get<ClientData>('cuenta/cliente') as Promise<ApiLike<ClientData>>;
    }

    // --- Credit ---
    static async getCreditStatus() {
        return Api.get<CreditStatus>('cuenta/credito') as Promise<ApiLike<CreditStatus>>;
    }

    static async getCreditDetail() {
        return Api.get<any>('cuenta/credito/detalle') as Promise<ApiLike<any>>;
    }

    static async verifyCredit(monto: number) {
        return Api.post<CreditVerification>('cuenta/credito/verificar', { monto }) as Promise<ApiLike<CreditVerification>>;
    }

    // --- Current Account ---
    static async getCurrentAccountStatus(idempresa?: number) {
        const query = idempresa ? `?idempresa=${idempresa}` : '';
        return Api.get<CurrentAccountStatus>(`cuenta/cuenta-corriente${query}`) as Promise<ApiLike<CurrentAccountStatus>>;
    }

    static async getCurrentAccountBalance() {
        return Api.get<{ saldo: number }>('cuenta/cuenta-corriente/saldo') as Promise<ApiLike<{ saldo: number }>>;
    }

    static async getCurrentAccountMovements(idempresa?: number, fechaDesde?: string, fechaHasta?: string) {
        const params = new URLSearchParams();
        if (idempresa) params.set('idempresa', String(idempresa));
        if (fechaDesde) params.set('fecha_desde', fechaDesde);
        if (fechaHasta) params.set('fecha_hasta', fechaHasta);
        const query = params.toString() ? `?${params.toString()}` : '';
        return Api.get<CurrentAccountMovement[]>(`cuenta/cuenta-corriente/movimientos${query}`) as Promise<ApiLike<CurrentAccountMovement[]>>;
    }

    static async getCurrentAccountCache() {
        return Api.get<any>('cuenta/cuenta-corriente/cache') as Promise<ApiLike<any>>;
    }

    static async clearCurrentAccountCache() {
        return Api.delete<any>('cuenta/cuenta-corriente/cache') as Promise<ApiLike<any>>;
    }

    // --- Vouchers (Comprobantes) ---
    static async getVouchers(desde?: string, hasta?: string) {
        let query = '';
        if (desde && hasta) {
            query = `?desde=${desde}&hasta=${hasta}`;
        }
        return Api.get<Voucher[]>(`cuenta/comprobantes${query}`) as Promise<ApiLike<Voucher[]>>;
    }

    static async getVouchersCurrentMonth() {
        return Api.get<Voucher[]>('cuenta/comprobantes/mes-actual') as Promise<ApiLike<Voucher[]>>;
    }

    static async getVoucherDetail(idventa: number) {
        return Api.get<any>(`cuenta/comprobantes/${idventa}`) as Promise<ApiLike<any>>;
    }

    static async downloadVoucherPdf(idventa: number) {
        return Api.url(`cuenta/comprobantes/${idventa}/pdf`);
    }

    static async getVouchersExportTxt() {
        return Api.get<any>('cuenta/comprobantes/exportar/txt') as Promise<ApiLike<any>>;
    }

    static async getVouchersExportExcel() {
        return Api.get<any>('cuenta/comprobantes/exportar/excel') as Promise<ApiLike<any>>;
    }

    static async getVouchersExportDbf() {
        return Api.get<any>('cuenta/comprobantes/exportar/dbf') as Promise<ApiLike<any>>;
    }

    static async getVouchersExportDetailTxt() {
        return Api.post<any>('cuenta/comprobantes/exportar/detalle-txt') as Promise<ApiLike<any>>;
    }

    static async getVouchersDetail() {
        return Api.get<any>('cuenta/comprobantes/detalle') as Promise<ApiLike<any>>;
    }

    static async getVouchersCache() {
        return Api.get<any>('cuenta/comprobantes/cache') as Promise<ApiLike<any>>;
    }

    static async clearVouchersCache() {
        return Api.delete<any>('cuenta/comprobantes/cache') as Promise<ApiLike<any>>;
    }

    // --- Summaries (Resúmenes) ---
    static async getSummaries(filters?: { idempresa?: number }) {
        let qs = '';
        if (filters && filters.idempresa) {
            const params = new URLSearchParams();
            params.append('idempresa', String(filters.idempresa));
            qs = `?${params.toString()}`;
        }
        return Api.get<Summary[]>(`cuenta/resumenes${qs}`) as Promise<ApiLike<Summary[]>>;
    }

    static async getSummaryDetail(id: number) {
        return Api.get<any>(`cuenta/resumenes/${id}`) as Promise<ApiLike<any>>;
    }

    static async getSummariesCurrentMonth() {
        return Api.get<Summary[]>('cuenta/resumenes/mes-actual') as Promise<ApiLike<Summary[]>>;
    }

    static async downloadSummaryPdf(id: number) {
        return Api.url(`cuenta/resumenes/${id}/pdf`);
    }

    static async getSummariesCache() {
        return Api.get<any>('cuenta/resumenes/cache') as Promise<ApiLike<any>>;
    }

    static async clearSummariesCache() {
        return Api.delete<any>('cuenta/resumenes/cache') as Promise<ApiLike<any>>;
    }

    // --- Receipts (Recibos) ---
    static async getReceipts(filters?: { idempresa?: number; mes?: number; anio?: number }) {
        let qs = '';
        if (filters) {
            const params = new URLSearchParams();
            if (filters.idempresa) params.append('idempresa', String(filters.idempresa));
            if (filters.mes) params.append('mes', String(filters.mes));
            if (filters.anio) params.append('anio', String(filters.anio));
            if ([...params].length > 0) qs = `?${params.toString()}`;
        }
        return Api.get<Receipt[]>(`cuenta/recibos${qs}`) as Promise<ApiLike<Receipt[]>>;
    }

    static async downloadReceiptPdf(id: number) {
        return Api.url(`cuenta/recibos/${id}/pdf`);
    }

    // --- Card Settlements (Liquidaciones Tarjetas) ---
    static async getCardSettlements() {
        return Api.get<CardSettlement[]>('cuenta/liquidaciones-tarjetas') as Promise<ApiLike<CardSettlement[]>>;
    }

    static async getEnabledCards() {
        return Api.get<EnabledCard[]>('cuenta/liquidaciones-tarjetas/tarjetas-habilitadas') as Promise<ApiLike<EnabledCard[]>>;
    }

    // --- Claims (Reclamos) ---
    static async getClaims(filters?: { estado?: string; motivo?: string; search?: string; page?: number }) {
        let query = new URLSearchParams();
        if (filters?.estado && filters.estado !== 'todos') query.append('estado', filters.estado);
        if (filters?.motivo && filters.motivo !== 'todos') query.append('motivo', filters.motivo);
        if (filters?.search) query.append('search', filters.search);
        if (filters?.page) query.append('page', String(filters.page));

        const queryString = query.toString() ? `?${query.toString()}` : '';
        return Api.get<Claim[]>(`incidencias/reclamos${queryString}`) as Promise<ApiLike<Claim[]> & { meta?: any }>;
    }

    static async createClaim(data: any) {
        return Api.post<{ id: number; estado: string }>('incidencias/reclamos', data) as Promise<ApiLike<{ id: number; estado: string }>>;
    }

    static async getClaimDetail(id: number) {
        return Api.get<any>(`incidencias/reclamos/${id}`) as Promise<ApiLike<any>>;
    }

    static async downloadClaimVoucherPdf(id: number) {
        return Api.url(`incidencias/reclamos/${id}/comprobante`);
    }

    static async cancelClaim(id: number, observacion: string) {
        return Api.put<any>(`incidencias/reclamos/${id}/cancelar`, { observacion }) as Promise<ApiLike<any>>;
    }

    static async markClaimTasksAsRead(id: number) {
        return Api.put<any>('incidencias/reclamos/marcar-leidos', { idreclamo: id }) as Promise<ApiLike<any>>;
    }

    static async getClaimTypes() {
        return Api.get<ClaimReason[]>('incidencias/reclamos/tipos') as Promise<ApiLike<ClaimReason[]>>;
    }

    static async getClaimLaboratories() {
        return Api.get<ClaimLaboratory[]>('incidencias/reclamos/laboratorios-habilitados') as Promise<ApiLike<ClaimLaboratory[]>>;
    }

    // --- Helper for Claims Wizard ---
    static async findInvoice(params: { letra: string; puntoEmision: string; numero: string }) {
        const query = new URLSearchParams({
            letra: params.letra,
            puntoEmision: params.puntoEmision,
            numero: params.numero
        });
        return Api.get<any[]>(`comprobantes/unComprobante?${query.toString()}`) as Promise<ApiLike<any[]>>;
    }

    // --- Payments (Pagos) ---
    static async registerPayment(data: PaymentRegistration | FormData) {
        return Api.post<{ message: string }>('cuenta/pagos', data) as Promise<ApiLike<{ message: string }>>;
    }

    static async reportPayment(data: PaymentReport | FormData) {
        // Enforce FormData or PaymentReport. If generic 'data' is passed, ensure it matches
        return Api.post<any>('cuentas/informarPago', data) as Promise<ApiLike<any>>;
    }
}
